/**
 * The prompt bar at the bottom of the Audio Studio banner (Studio tab) — a white surface on the dark banner, a two-column grid: on the left a one-line "describe the sound" input
 * (grows to 4 lines while typing) with the chip row under it (same left edge); on the right Generate, vertically centred
 * on that stack (full width under it on phones). The chip row holds ONE summary chip ("30s · Voice-over bed · Instrumental ▾") that opens a popover with
 * every option — ad length (6 / 15 / 30 / 60s), vocals, use case (quick choices led by "Voice-over bed", plus the full
 * list), templates, genre, mood, tempo, energy, instruments, variations, lyrics, name. Length, vocals and use case are
 * remembered between visits (localStorage).
 * Generate is the page's only solid-accent button; it stays enabled but looks neutral until there is something to
 * generate from (text, or a template / genre / mood) and calls POST /studio/v1/track/create. The bar is for free-form prompts only —
 * remixing a trend happens in RemixDialog. The job lands in "Recent creations", and once the request is accepted the bar goes
 * back to idle (text cleared, the user's own settings kept); a failed request leaves everything as it was.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { toastStudioError } from '@/cowork/launch/studioErrors';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/components/ui/utils';
import type { StudioPresets } from '@/api/audioStudioApi';
import { CreateOptions, Segmented, labelCls, selectCls } from './CreateOptions';
import {
  AD_DURATIONS,
  FALLBACK_PRESETS,
  VOICEOVER_BED,
  PROMPT_MAX,
  buildGenerateBody,
  canGenerate,
  loadInitialForm,
  saveSettings,
  type CreateFormState,
} from './createForm';
import { useStudioBrands } from './brands';
import { usePresets } from './queries';
import { startJobRequest, type StudioJobRef, type StudioJobRequest } from './studioJobs';

/** Quick use-case choices after "Voice-over bed" (when the presets offer them). */
const QUICK_USE_CASES = ['tiktok_hook', 'product_demo', 'tiktok'];
/** Input line height (px) and the most lines it grows to before scrolling. */
const LINE_PX = 24;
const MAX_LINES = 4;
/** The character counter only shows from this share of the limit. */
const COUNTER_FROM = 0.8;

function labelOf(options: { id: string; label: string }[], id: string): string {
  return options.find((o) => o.id === id)?.label || FALLBACK_PRESETS.use_cases.find((o) => o.id === id)?.label || id;
}

/** "Voice-over bed" first, then up to two more ad-shaped use cases the service knows about. */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, unit-tested with the bar
export function quickUseCases(presets: StudioPresets): { id: string; label: string }[] {
  const out = [{ id: VOICEOVER_BED, label: labelOf(presets.use_cases, VOICEOVER_BED) }];
  for (const id of QUICK_USE_CASES) {
    const o = presets.use_cases.find((u) => u.id === id);
    if (o && out.length < 3) out.push({ id: o.id, label: o.label });
  }
  return out;
}

/** The style part of the summary chip: the template, else genre / mood, else "Any style". */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, unit-tested with the bar
export function styleSummary(form: CreateFormState, presets: StudioPresets): string {
  const tpl = form.presetId ? presets.templates?.find((t) => t.id === form.presetId) : undefined;
  if (tpl) return tpl.label;
  const parts = [form.genre && labelOf(presets.genres, form.genre), form.mood && labelOf(presets.moods, form.mood)].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Any style';
}

// eslint-disable-next-line react-refresh/only-export-components -- pure helper, unit-tested with the bar
export function optionsSummary(form: CreateFormState, presets: StudioPresets): string {
  const style = styleSummary(form, presets);
  return [
    `${form.duration}s`,
    form.useCase ? labelOf(presets.use_cases, form.useCase) : null,
    form.vocals ? 'Vocals' : 'Instrumental',
    style === 'Any style' ? null : style,
  ].filter(Boolean).join(' · ');
}

export function PromptBar({ onJobStarted, inputRef }: {
  onJobStarted: (job: StudioJobRef) => void;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
}) {
  const presetsQ = usePresets();
  // The sound is made for the active brand (the banner's "For <brand>" chip).
  const { activeBrandId } = useStudioBrands();
  const presets = presetsQ.data ?? FALLBACK_PRESETS;
  const [form, setFormState] = useState<CreateFormState>(loadInitialForm);
  const [hint, setHint] = useState(false);
  const set = useCallback(<K extends keyof CreateFormState>(k: K, v: CreateFormState[K]) => setFormState((f) => ({ ...f, [k]: v })), []);
  const setForm = useCallback((fn: (f: CreateFormState) => CreateFormState) => setFormState(fn), []);

  const ownRef = useRef<HTMLTextAreaElement | null>(null);
  const setInputEl = useCallback((el: HTMLTextAreaElement | null) => {
    ownRef.current = el;
    if (inputRef) inputRef.current = el;
  }, [inputRef]);

  // Remember length / vocals / use case for next time.
  useEffect(() => { saveSettings(form); }, [form.duration, form.vocals, form.useCase]);


  // Auto-grow: one line when empty, up to MAX_LINES while typing, then it scrolls.
  useLayoutEffect(() => {
    const el = ownRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = LINE_PX * MAX_LINES + 16;
    const next = Math.min(el.scrollHeight || LINE_PX + 16, max);
    el.style.height = `${Math.max(next, LINE_PX + 16)}px`;
    el.style.overflowY = (el.scrollHeight || 0) > max ? 'auto' : 'hidden';
  }, [form.prompt]);

  const generate = useMutation({
    mutationFn: ({ request }: { f: CreateFormState; request: StudioJobRequest }) => startJobRequest(request),
    onSuccess: (job, { f, request }) => {
      const typed = f.prompt.trim();
      onJobStarted({
        jobId: job.job_id,
        label: f.title.trim() || (typed.length > 60 ? `${typed.slice(0, 60)}…` : typed) || undefined,
        durationS: f.duration,
        request,
      });
      toast.success('Generating: follow it in Recent creations');
      // Accepted: back to the idle bar — no text / name. Length, vocals, use case and style choices are kept. On error
      // nothing is reset, so what they typed is still there.
      setFormState((cur) => ({ ...cur, prompt: '', title: '' }));
      setHint(false);
      ownRef.current?.blur();
    },
    onError: (e: Error) => toastStudioError(e, 'audio', 'Could not start the generation'),
  });

  const ready = canGenerate(form);
  const submit = () => {
    if (generate.isPending) return;
    if (!ready) {
      // Enabled but nothing to go on yet: point at the input instead of failing silently.
      setHint(true);
      ownRef.current?.focus();
      return;
    }
    setHint(false);
    const request: StudioJobRequest = { kind: 'generate', body: { ...buildGenerateBody(form), ...(activeBrandId ? { brand_id: activeBrandId } : {}) } };
    generate.mutate({ f: form, request });
  };

  const summary = optionsSummary(form, presets);
  const showCounter = form.prompt.length >= Math.floor(PROMPT_MAX * COUNTER_FROM);

  return (
    <form
      aria-label="Create a sound"
      className="grid grid-cols-1 items-center gap-x-3 gap-y-2 rounded-[14px] bg-white p-3 shadow-[0_4px_16px_rgba(16,24,40,0.18)] transition-shadow focus-within:ring-2 focus-within:ring-white/40 sm:grid-cols-[1fr_auto]"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      {/* Left: the input, then the chip row — both px-3, so the text and the chips share one left edge. */}
      <div data-prompt-part="stack" className="min-w-0">
        <label htmlFor="audio-prompt" className="sr-only">Describe the sound you want</label>
        <textarea
          id="audio-prompt"
          ref={setInputEl}
          value={form.prompt}
          onChange={(e) => { set('prompt', e.target.value.slice(0, PROMPT_MAX)); if (hint) setHint(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); } }}
          rows={1}
          aria-describedby={hint ? 'audio-prompt-hint' : undefined}
          placeholder="Describe the sound you want… or pick a trend below"
          className="block h-10 w-full resize-none bg-transparent px-3 py-2 text-[15px] leading-6 text-[#101828] placeholder:text-[#98a2b3] focus:outline-none"
          style={{ height: LINE_PX + 16 }}
        />
        <div data-prompt-part="chips" className="mt-1 flex h-7 min-w-0 items-center gap-2 px-3">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Options: ${summary}`}
                className="inline-flex h-7 min-w-0 max-w-full items-center gap-1 rounded-[8px] border border-[#eaecf0] bg-white px-2.5 text-[12.5px] text-[#475467] hover:bg-[#f9fafb] data-[state=open]:bg-[#f2f4f7]"
              >
                <span className="truncate">{summary}</span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="max-h-[min(70vh,560px)] w-[min(92vw,480px)] overflow-y-auto rounded-[12px] p-4">
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className={labelCls} style={{ fontWeight: 600 }}>Length</div>
                  <Segmented label="Length" value={form.duration} options={AD_DURATIONS} onChange={(v) => set('duration', v)} format={(v) => `${v}s`} />
                </div>
                <div>
                  <div className={labelCls} style={{ fontWeight: 600 }}>Vocals</div>
                  <Segmented
                    label="Vocals"
                    value={form.vocals ? 'vocals' : 'instrumental'}
                    options={['instrumental', 'vocals'] as const}
                    onChange={(v) => set('vocals', v === 'vocals')}
                    format={(v) => (v === 'vocals' ? 'Vocals' : 'Instrumental')}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="audio-use-case" className={labelCls} style={{ fontWeight: 600 }}>Use case</label>
                  <div role="group" aria-label="Quick use cases" className="mb-2 flex flex-wrap gap-1.5">
                    {quickUseCases(presets).map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        aria-pressed={form.useCase === o.id}
                        onClick={() => set('useCase', form.useCase === o.id ? '' : o.id)}
                        className={cn('rounded-full border px-2.5 py-1 text-[12px] disabled:opacity-50', form.useCase === o.id ? 'border-[#344054] bg-[#f2f4f7] text-[#101828]' : 'border-[#eaecf0] bg-white text-[#475467] hover:bg-[#f9fafb]')}
                        style={{ fontWeight: 500 }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <select id="audio-use-case" value={form.useCase} onChange={(e) => set('useCase', e.target.value)} className={selectCls}>
                    <option value="">Any use case</option>
                    {presets.use_cases.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    {form.useCase && !presets.use_cases.some((o) => o.id === form.useCase) && <option value={form.useCase}>{labelOf(presets.use_cases, form.useCase)}</option>}
                  </select>
                </div>
              </div>
              <CreateOptions form={form} set={set} setForm={setForm} presets={presets} />
            </PopoverContent>
          </Popover>

          {showCounter && (
            <span className={cn('ml-auto shrink-0 text-[11.5px] leading-4 tabular-nums', form.prompt.length >= PROMPT_MAX ? 'text-[#b42318]' : 'text-[#98a2b3]')} aria-live="polite">
              {form.prompt.length}/{PROMPT_MAX}
            </span>
          )}
        </div>
        {hint && !ready && (
          <p id="audio-prompt-hint" className="mt-1 px-3 text-[12.5px] leading-[18px] text-[#667085]" role="status">
            Describe the sound, pick a trend below, or choose a style in the options.
          </p>
        )}
      </div>

      {/* Right: Generate, vertically centred on the stack (full width under it on phones). */}
      <Button
        type="submit"
        disabled={generate.isPending}
        data-ready={ready ? 'true' : 'false'}
        className={cn(
          'h-10 w-full shrink-0 gap-1.5 rounded-[8px] px-4 text-[14px] transition-colors sm:w-auto',
          ready ? 'bg-[#be43ff] text-white hover:bg-[#a835e0]' : 'bg-[#f2f4f7] text-[#667085] shadow-none hover:bg-[#eaecf0]',
        )}
      >
        {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Wand2 className="h-4 w-4" aria-hidden />}
        Generate{form.variations > 1 ? ` ×${form.variations}` : ''}
      </Button>
    </form>
  );
}
